import { z } from "zod";

import {
  evalAgentRuntimeSchema,
  evalAgentVariantSchema,
  evalExecutionModeSchema,
  evalScenarioIdSchema,
} from "@/shared/evals/run-spec";
import { ConfigSubdomain } from "@/shared/group-configs/config-subdomains";
import { VOICE_GROUP_IDS } from "@/shared/group-configs/groups";
import { GroupWorkflow } from "@/shared/voice-runtime";

export const EVAL_SCENARIO_CATALOG_VERSION = 3;
export const SHARED_EVAL_GROUP_SCOPE = "shared";
export const EVAL_GROUP_CONFIG_SUBDOMAINS = [
  ConfigSubdomain.AXIA,
  ConfigSubdomain.CONVENIENT_MD,
  ConfigSubdomain.ENT_PARTNERS,
  ConfigSubdomain.ENT_PARTNERS_SCI,
  ConfigSubdomain.NIMBUS,
  ConfigSubdomain.NIRA,
  ConfigSubdomain.PMG,
  ConfigSubdomain.SPORTORTHO,
  ConfigSubdomain.TPR,
  ConfigSubdomain.UPPERLINE_GEORGIA,
  ConfigSubdomain.VALERIE,
] as const;
export const EVAL_GROUP_SCOPES = [SHARED_EVAL_GROUP_SCOPE, ...EVAL_GROUP_CONFIG_SUBDOMAINS] as const;
export const EVAL_WORKFLOWS = Object.values(GroupWorkflow);

/** Supported voice tenant config subdomain, or `shared` for group-independent scenarios. */
export const evalGroupScopeSchema = z.enum(EVAL_GROUP_SCOPES);
export const evalGroupConfigSubdomainSchema = z.enum(EVAL_GROUP_CONFIG_SUBDOMAINS);
export const evalWorkflowSchema = z.enum(GroupWorkflow);

export type EvalGroupScope = z.infer<typeof evalGroupScopeSchema>;
export type EvalGroupConfigSubdomain = z.infer<typeof evalGroupConfigSubdomainSchema>;
export type EvalWorkflow = z.infer<typeof evalWorkflowSchema>;

const workflowApplicabilitySchema = z
  .object({
    kind: z.literal("workflows"),
    requiredWorkflows: z.array(evalWorkflowSchema).min(1),
  })
  .strict();

const explicitGroupApplicabilitySchema = z
  .object({
    kind: z.literal("groups"),
    groupIds: z.array(z.enum(VOICE_GROUP_IDS)).min(1),
  })
  .strict();

const groupSpecificApplicabilitySchema = z
  .object({
    kind: z.literal("group-specific"),
    groupScope: evalGroupConfigSubdomainSchema,
  })
  .strict();

export const evalScenarioApplicabilitySchema = z.discriminatedUnion("kind", [
  workflowApplicabilitySchema,
  explicitGroupApplicabilitySchema,
  groupSpecificApplicabilitySchema,
]);

export const evalGroupCatalogTargetSchema = z
  .object({
    groupId: z.enum(VOICE_GROUP_IDS),
    configSubdomain: evalGroupConfigSubdomainSchema,
    workflows: z.array(evalWorkflowSchema),
    agentVariants: z.array(evalAgentVariantSchema).min(1),
  })
  .strict();

export const evalScenarioCatalogEntrySchema = z
  .object({
    id: evalScenarioIdSchema,
    packId: evalScenarioIdSchema.optional(),
    kind: z.enum(["scripted", "simulated"]),
    description: z.string().optional(),
    tags: z.array(z.string()),
    supportedModes: z.array(evalExecutionModeSchema).min(1),
    agentVariants: z.array(evalAgentVariantSchema).min(1),
    // Optional so the control plane can read catalogs created before runtime was explicit.
    agentRuntimes: z.partialRecord(evalAgentVariantSchema, evalAgentRuntimeSchema).optional(),
    resultVariantIds: z.partialRecord(evalAgentVariantSchema, z.string().min(1)),
    groupScope: evalGroupScopeSchema,
    groupId: z.enum(VOICE_GROUP_IDS).optional(),
    applicability: evalScenarioApplicabilitySchema,
    applicableGroupScopes: z.array(evalGroupConfigSubdomainSchema).min(1),
    applicableGroupIds: z.array(z.enum(VOICE_GROUP_IDS)).min(1),
    scoreThreshold: z.number().min(0).max(1),
    /**
     * Variants on which the scenario's expectation is recorded but not gated. Optional so the control
     * plane can read catalogs created before pending expectations existed.
     */
    pendingVariants: z.array(evalAgentVariantSchema).optional(),
    maxTurns: z.number().int().positive().optional(),
  })
  .strict()
  .superRefine((entry, ctx) => {
    if (new Set(entry.agentVariants).size !== entry.agentVariants.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "agentVariants must not contain duplicates",
        path: ["agentVariants"],
      });
    }

    const mappedVariants = Object.keys(entry.resultVariantIds);

    if (
      mappedVariants.length !== entry.agentVariants.length ||
      entry.agentVariants.some((variant) => !entry.resultVariantIds[variant])
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "resultVariantIds must map every supported agent variant exactly once",
        path: ["resultVariantIds"],
      });
    }

    if (entry.agentRuntimes) {
      const runtimeVariants = Object.keys(entry.agentRuntimes);

      if (
        runtimeVariants.length !== entry.agentVariants.length ||
        entry.agentVariants.some((variant) => !entry.agentRuntimes?.[variant])
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "agentRuntimes must map every supported agent variant exactly once",
          path: ["agentRuntimes"],
        });
      }
    }

    if (entry.pendingVariants) {
      if (new Set(entry.pendingVariants).size !== entry.pendingVariants.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "pendingVariants must not contain duplicates",
          path: ["pendingVariants"],
        });
      }

      // A pending variant the scenario never runs is silently inert: it reads as "we know this lane
      // is red" while nothing is measured there, so reject it rather than let the marker rot.
      const unrun = entry.pendingVariants.filter((variant) => !entry.agentVariants.includes(variant));

      if (unrun.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `pendingVariants must be a subset of agentVariants; ${unrun.join(", ")} is not run`,
          path: ["pendingVariants"],
        });
      }
    }

    if (entry.groupScope === SHARED_EVAL_GROUP_SCOPE && entry.groupId !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "shared scenarios must not declare a groupId",
        path: ["groupId"],
      });
    }

    if (entry.groupScope !== SHARED_EVAL_GROUP_SCOPE && entry.groupId === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "group-scoped scenarios must declare a groupId",
        path: ["groupId"],
      });
    }

    if (new Set(entry.applicableGroupScopes).size !== entry.applicableGroupScopes.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "applicableGroupScopes must not contain duplicates",
        path: ["applicableGroupScopes"],
      });
    }

    if (new Set(entry.applicableGroupIds).size !== entry.applicableGroupIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "applicableGroupIds must not contain duplicates",
        path: ["applicableGroupIds"],
      });
    }

    if (entry.groupScope === SHARED_EVAL_GROUP_SCOPE && entry.applicability.kind === "group-specific") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "shared scenarios must declare workflow or explicit-group applicability",
        path: ["applicability"],
      });
    }

    if (entry.groupScope !== SHARED_EVAL_GROUP_SCOPE) {
      if (
        entry.applicability.kind !== "group-specific" ||
        entry.applicability.groupScope !== entry.groupScope ||
        entry.applicableGroupScopes.length !== 1 ||
        entry.applicableGroupScopes[0] !== entry.groupScope ||
        entry.groupId === undefined ||
        !entry.applicableGroupIds.includes(entry.groupId)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "group-scoped scenarios must apply to their authored group within one canonical group scope",
          path: ["applicability"],
        });
      }
    }
  });

export const evalScenarioCatalogSchema = z
  .object({
    version: z.literal(EVAL_SCENARIO_CATALOG_VERSION),
    sourceGitSha: z
      .string()
      .regex(/^[a-f0-9]{40}$/i)
      .optional(),
    groups: z.array(evalGroupCatalogTargetSchema),
    scenarios: z.array(evalScenarioCatalogEntrySchema),
  })
  .strict();

export type EvalScenarioCatalog = z.infer<typeof evalScenarioCatalogSchema>;
export type EvalScenarioCatalogEntry = z.infer<typeof evalScenarioCatalogEntrySchema>;
export type EvalGroupCatalogTarget = z.infer<typeof evalGroupCatalogTargetSchema>;
